import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DayStatus } from "@sundial/core";
import { api, apiUpload } from "./client.js";

export interface Me {
  id: string;
  email: string;
}
export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api<Me>("/auth/me").catch(() => null),
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { email: string; password: string }) =>
      api<{ me: Me }>("/auth/login", { method: "POST", body: JSON.stringify(b) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/auth/logout", { method: "POST" }),
    onSuccess: () => qc.setQueryData(["me"], null),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (b: { currentPassword: string; newPassword: string }) =>
      api<{ ok: boolean }>("/auth/password", { method: "POST", body: JSON.stringify(b) }),
  });
}

export interface DayAllocation {
  clientId: string;
  status: "full" | "half";
}
export interface MonthData {
  days: {
    date: string;
    status: DayStatus;
    note: string | null;
    source: string;
    clientId: string | null;
    /** Extra clients billed on this date, beyond the primary clientId. */
    allocations: DayAllocation[];
  }[];
  summary: { full: number; half: number; none: number; sick: number; holiday: number; billable: number };
  missed: string[];
}
export function useMonth(month: string) {
  return useQuery({ queryKey: ["month", month], queryFn: () => api<MonthData>(`/days/month?month=${month}`) });
}

export function useLogDay(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: {
      date: string;
      status: DayStatus;
      note?: string;
      clientId?: string;
      allocations?: DayAllocation[];
    }) => api("/days", { method: "PUT", body: JSON.stringify(b) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["month", month] }),
  });
}

export function useClearDay(month: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => api(`/days/${date}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["month", month] }),
  });
}

export interface Payment {
  id: string;
  expectedCents: number;
  receivedCents: number | null;
  status: "received" | "pending" | "not_landed";
  expectedDate: string | null;
  receivedDate: string | null;
  period: string | null;
  note: string | null;
  /** The Abby invoice this payment settles — a reference the user enters. */
  invoiceRef: string | null;
  invoiceUrl: string | null;
  clientId: string | null;
}
export function usePayments() {
  return useQuery({
    queryKey: ["payments"],
    queryFn: () => api<{ payments: Payment[]; totalOwedCents: number }>("/payments"),
  });
}
export function useCreatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { expectedCents: number; expectedDate?: string; period?: string; clientId?: string }) =>
      api("/payments", { method: "POST", body: JSON.stringify(b) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}
export function useMarkReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { id: string; receivedCents: number; receivedDate: string }) =>
      api(`/payments/${b.id}/received`, {
        method: "POST",
        body: JSON.stringify({ receivedCents: b.receivedCents, receivedDate: b.receivedDate }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}

export function useUpdatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: {
      id: string;
      expectedCents?: number;
      expectedDate?: string | null;
      period?: string | null;
      note?: string | null;
      receivedCents?: number;
      receivedDate?: string | null;
      invoiceRef?: string | null;
      invoiceUrl?: string | null;
      clientId?: string | null;
    }) =>
      api(`/payments/${b.id}`, {
        method: "PUT",
        body: JSON.stringify({
          expectedCents: b.expectedCents,
          expectedDate: b.expectedDate,
          period: b.period,
          note: b.note,
          receivedCents: b.receivedCents,
          receivedDate: b.receivedDate,
          invoiceRef: b.invoiceRef,
          invoiceUrl: b.invoiceUrl,
          clientId: b.clientId,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}

export function useDeletePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/payments/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payments"] }),
  });
}

export interface Client {
  id: string;
  name: string;
  siret: string;
  address: string;
  tjmCents: number;
  archived: boolean;
}
export function useClients() {
  return useQuery({
    queryKey: ["clients"],
    queryFn: () => api<{ clients: Client[] }>("/clients"),
  });
}
export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { name: string; siret?: string; address?: string; tjmCents: number }) =>
      api("/clients", { method: "POST", body: JSON.stringify(b) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });
}
export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: {
      id: string;
      name?: string;
      siret?: string;
      address?: string;
      tjmCents?: number;
      archived?: boolean;
    }) =>
      api(`/clients/${b.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: b.name,
          siret: b.siret,
          address: b.address,
          tjmCents: b.tjmCents,
          archived: b.archived,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });
}
export function useSetDefaultClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/clients/${id}/default`, { method: "PUT" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}
export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/clients/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clients"] }),
  });
}

export interface Settings {
  tjmCents: number;
  halfDayWeight: number;
  frHolidaysEnabled: boolean;
  theme: string;
  dailyPingTime: string;
  paymentReminderDay: number;
  paymentReminderTime: string;
  urssafEnabled: boolean;
  /** Override for the statutory URSSAF rate. null = use the period schedule. */
  urssafRatePct: number | null;
  microCeilingCents: number;
  vatFranchiseCents: number;
  /** celibataire | marie | pacse | divorce | veuf */
  foyerSituation: string;
  children: number;
  childrenGardeAlternee: number;
  parentIsole: boolean;
  spouseIncomeCents: number;
  otherIncomeCents: number;
  /** bnc | bic_services | bic_vente */
  microRegime: string;
  /** Real taux personnalisé from impots.gouv.fr. null = use the barème estimate. */
  tauxPersoPct: number | null;
  /** Google Sheet to mirror into. null = fall back to the SHEETS_SPREADSHEET_ID env. */
  sheetsSpreadsheetId: string | null;
  /** Whether to sync automatically (1st of the month + on payment received). */
  sheetsAutoSync: boolean;
  /**
   * Read-only status derived server-side: the service account's client_email,
   * or null. The key itself is write-only and never sent to the client.
   */
  sheetsConnectedAs: string | null;
  /** Freelance identity for the relevé PDF. */
  bizName: string;
  bizSiret: string;
  bizAddress: string;
  /** The client new days / quick-logs default to. */
  defaultClientId: string | null;
  /** Déclaration & échéances rules — drive the "À venir" deadlines. */
  urssafDeclarationFreq: "monthly" | "quarterly";
  versementLiberatoire: boolean;
  tvaRegime: "franchise" | "reel_simplifie" | "reel_normal";
  businessStartYear: number | null;
  tvaDeclarationDay: number;
  urssafMonthlyDay: number | null;
  onboarded: boolean;
  dailyPingTimes: string;
  dailyPingEveryday: boolean;
  repeatEnabled: boolean;
  repeatEveryMinutes: number;
  repeatUntil: string;
}

export interface UrssafSummary {
  enabled: boolean;
  ratePct: number;
  receivedYtdCents: number;
  setAsideCents: number;
  ceilingCents: number;
  ceilingPct: number;
  vatFranchiseCents: number;
  vatPct: number;
  vatApplicable: boolean;
}
export function useUrssaf() {
  return useQuery({ queryKey: ["urssaf"], queryFn: () => api<UrssafSummary>("/urssaf") });
}
export function useSettings() {
  return useQuery({ queryKey: ["settings"], queryFn: () => api<Settings>("/settings") });
}

export interface Deadline {
  kind: "urssaf" | "tva" | "cfe" | "impot";
  dueDate: string;
  periodLabel: string;
  note?: string;
  amountHintCents: number | null;
}
export function useDeadlines() {
  return useQuery({ queryKey: ["deadlines"], queryFn: () => api<{ deadlines: Deadline[] }>("/deadlines") });
}
export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Settings>) => api("/settings", { method: "PUT", body: JSON.stringify(patch) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["settings"] });
      void qc.invalidateQueries({ queryKey: ["urssaf"] }); // rate/ceiling/VAT changes recompute the dashboard card
    },
  });
}

export interface Schedule {
  id: string;
  kind: "daily_ping" | "payment_reminder";
  cron: string;
  enabled: boolean;
}
export function useSchedules() {
  return useQuery({
    queryKey: ["schedules"],
    queryFn: () => api<{ schedules: Schedule[] }>("/settings/schedules"),
  });
}
export function useUpsertSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { id?: string; kind: Schedule["kind"]; cron: string; enabled: boolean }) =>
      api("/settings/schedules", { method: "PUT", body: JSON.stringify(b) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }),
  });
}

export function useTelegramStatus(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ["telegram-status"],
    queryFn: () => api<{ connected: boolean; botUsername: string | null }>("/settings/telegram/status"),
    refetchInterval: options?.refetchInterval,
  });
}

export function useGenerateTelegramCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ code: string }>("/settings/telegram/code", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["telegram-status"] }),
  });
}

export function useDisconnectTelegram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api("/settings/telegram/disconnect", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["telegram-status"] }),
  });
}

export function useTestPing() {
  return useMutation({
    mutationFn: () =>
      api<{ result: "sent" | "not-connected" | "bot-disabled" }>("/settings/telegram/test", { method: "POST" }),
  });
}

export interface DaysImportPreview {
  months: { month: string; target: number; existing: number; toAdd: number; warnings: string[] }[];
  rowErrors: { line: number; message: string }[];
  totalToAdd: number;
  committed: boolean;
}
export interface PaymentsImportPreview {
  rows: number;
  totalCents: number;
  rowErrors: { line: number; message: string }[];
  created: number;
  committed: boolean;
}

export function useImportDays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { file: File; dryRun: boolean }) =>
      apiUpload<DaysImportPreview>(`/days/import?dryRun=${b.dryRun ? 1 : 0}`, b.file),
    onSuccess: (res) => {
      if (res.committed) {
        qc.invalidateQueries({ queryKey: ["month"] });
        qc.invalidateQueries({ queryKey: ["report-range"] });
      }
    },
  });
}

export function useImportPayments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { file: File; dryRun: boolean }) =>
      apiUpload<PaymentsImportPreview>(`/payments/import?dryRun=${b.dryRun ? 1 : 0}`, b.file),
    onSuccess: (res) => {
      if (res.committed) {
        qc.invalidateQueries({ queryKey: ["payments"] });
        qc.invalidateQueries({ queryKey: ["report-range"] });
      }
    },
  });
}

export interface ReportRangeRow {
  date: string;
  status: string;
  billable: number;
  expectedCents: number;
}

export interface PaymentRangeRow {
  id: string;
  period: string | null;
  expectedCents: number;
  receivedCents: number | null;
  status: string;
  receivedDate: string | null;
  invoiceRef: string | null;
  invoiceUrl: string | null;
}

export interface AfterTaxEstimate {
  amountCents: number;
  tauxPct: number;
  /** "custom" = real taux from impots.gouv.fr; "simulated" = our bareme estimate. */
  source: "custom" | "simulated";
  baremeYearUsed: number | null;
  isEstimateFromPriorYear: boolean;
  /** Rate came from a run-rate projection, not a complete year — say so on screen. */
  isProjected: boolean;
  /** The projected annual HT the rate was derived from. */
  annualBasisHtCents: number;
}

export interface MonthRollup {
  month: string;
  billableDays: number;
  expectedHtCents: number;
  vatCents: number;
  expectedCents: number;
}

export interface ReportRangeData {
  rows: ReportRangeRow[];
  payments: PaymentRangeRow[];
  months: MonthRollup[];
  totals: {
    billableDays: number;
    expectedHtCents: number;
    vatCents: number;
    /** TTC — same basis as receivedCents. */
    expectedCents: number;
    receivedCents: number;
    owedCents: number;
    urssafCents: number;
    /** HT − URSSAF. NOT take-home — income tax is not deducted. */
    netCents: number;
    afterTax: AfterTaxEstimate | null;
    /** The same chain on cash actually received rather than invoiced. */
    receivedHtCents: number;
    receivedUrssafCents: number;
    receivedNetCents: number;
    receivedAfterTax: AfterTaxEstimate | null;
  };
  /** Present only when a client filter is active. Activity/billing for that
      client alone — never a tax split (VAT/URSSAF stay person-level). */
  clientActivity: {
    billableDays: number;
    expectedHtCents: number;
    receivedCents: number;
    owedCents: number;
  } | null;
  /** Present only unfiltered: HT/days per client, for the relevé's
      "Répartition par client" table. Null under a single-client filter. */
  clientBreakdown: { clientId: string; name: string; days: number; htCents: number }[] | null;
}

export function useReportRange(from: string, to: string, include: string = "both", clientId?: string) {
  const cid = clientId ?? "";
  return useQuery({
    queryKey: ["report-range", from, to, include, cid],
    queryFn: () =>
      api<ReportRangeData>(
        `/reports/range?from=${from}&to=${to}&include=${include}${cid ? `&clientId=${encodeURIComponent(cid)}` : ""}`,
      ),
    enabled: Boolean(from && to),
  });
}
